import { IValueInputAttachable } from "client/Forms/attachable";
import {
	getClientId
} from "client/Misc/clientId";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef
} from "react";
import {
	HubItemCategoryNodeFormModel,
	HubItemChildNodeFormModel,
	HubItemGroupNodeFormModel
} from "./common";
import { IconButton, ReorderableList } from "client/Components";
import {
	ensureNotNil,
	generateTrackingId
} from "client/Misc/utils";
import { HubSidebarCategoryItem } from "./HubSidebarCategoryItem";
import { HubSidebarItemContent } from "./HubSidebarItemContent";
import { Guid } from "client_legacy/ndaw/guid";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { GroupItemMenu } from "./GroupItemMenu";
import { HubItemCategoryNodeKind } from "client/Api/Hub/common";
import { useItemMenuState } from "client/Hooks";

export type HubSidebarGroupItemProps = {
	attachable: IValueInputAttachable<HubItemGroupNodeFormModel>;
	hubItemKind: HubItemKind;
	canReorderItems: boolean;
	groupsHierarchy: readonly HubItemGroupNodeFormModel[];
	containingGroupId?: Guid;
	isInTopLevel?: boolean;
	nodeClientIdToScrollTo: Guid | null;
	onNodeClientIdToScrollChange: (id: Guid | null) => void;
	doesMatchSearchTerm: (item: HubItemChildNodeFormModel) => boolean;
	onCreateCategory(clientId: Guid): void;
	onEditCategory(clientId: Guid): void;
	onDeleteCategory(clientId: Guid): void;
	onCreateGroup(clientId: Guid): void;
	onEditGroup: (clientId: Guid) => void;
	onDeleteGroup(clientId: Guid): void;
	onMoveToTop(clientId: Guid): void;
	onMoveToGroup(clientId: Guid, targetGroupId: Guid): void;
	onItemReorder: () => void;
};

export const HubSidebarGroupItem = (props: HubSidebarGroupItemProps): ReactElement => {
	const {
		attachable,
		isInTopLevel = false,
		canReorderItems,
		hubItemKind,
		groupsHierarchy,
		containingGroupId,
		nodeClientIdToScrollTo,
		onNodeClientIdToScrollChange,
		doesMatchSearchTerm,
		onCreateCategory,
		onEditCategory,
		onDeleteCategory,
		onCreateGroup,
		onEditGroup,
		onDeleteGroup,
		onMoveToTop,
		onMoveToGroup,
		onItemReorder
	} = props;

	const {
		groupData,
		name
	} = attachable.value;

	const {
		children
	} = groupData;

	const itemRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const menuButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const groupItemClientId: Guid = getClientId(attachable.value);

	const {
		menuState,
		onOpenMoreMenu,
		onOpenContextMenu,
		onCloseMenu
	} = useItemMenuState();

	useEffect(
		(): void => {
			if (nodeClientIdToScrollTo === groupItemClientId) {
				itemRef.current
					?.scrollIntoView();

				onNodeClientIdToScrollChange(null);
			}
		},
		[
			nodeClientIdToScrollTo,
			groupItemClientId,
			onNodeClientIdToScrollChange
		]
	);

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			onItemReorder();

			const itemsCopy: HubItemChildNodeFormModel[] = [...children];
			const item: HubItemChildNodeFormModel = itemsCopy.splice(fromIndex, 1)[0];
			itemsCopy.splice(toIndex, 0, item);

			attachable
				.for("groupData")
				.for("children")
				.reducer
				.setValue(itemsCopy);
		},
		[
			attachable,
			children,
			onItemReorder
		]
	);

	const getOverlayTextHandler = useCallback(
		(activeIndex: number): string => {
			const activeItem: HubItemChildNodeFormModel = ensureNotNil(children[activeIndex]);
			return activeItem.name;
		},
		[children]
	);

	const renderCategoryItem = (
		categoryItemHandler: IValueInputAttachable<HubItemCategoryNodeFormModel>
	): ReactElement =>{
		const clientId: Guid = getClientId(categoryItemHandler.value);

		return (
			<HubSidebarCategoryItem
				onMoveToTop={onMoveToTop}
				onMoveToGroup={onMoveToGroup}
				key={clientId}
				attachable={categoryItemHandler}
				onDelete={onDeleteCategory}
				onEdit={onEditCategory}
				containingGroupId={groupItemClientId}
				groupsHierarchy={groupsHierarchy}
				nodeClientIdToScrollTo={nodeClientIdToScrollTo}
				onNodeClientIdToScrollChange={onNodeClientIdToScrollChange}
			/>
		);
	};

	const renderGroupItem = (
		groupItemHandler: IValueInputAttachable<HubItemGroupNodeFormModel>,
	): ReactElement => {
		return (
			<HubSidebarGroupItem
				key={getClientId(groupItemHandler.value)}
				hubItemKind={hubItemKind}
				attachable={groupItemHandler}
				canReorderItems={canReorderItems}
				groupsHierarchy={groupsHierarchy}
				containingGroupId={groupItemClientId}
				nodeClientIdToScrollTo={nodeClientIdToScrollTo}
				onNodeClientIdToScrollChange={onNodeClientIdToScrollChange}
				doesMatchSearchTerm={doesMatchSearchTerm}
				onCreateCategory={onCreateCategory}
				onEditCategory={onEditCategory}
				onDeleteCategory={onDeleteCategory}
				onCreateGroup={onCreateGroup}
				onEditGroup={onEditGroup}
				onDeleteGroup={onDeleteGroup}
				onMoveToTop={onMoveToTop}
				onMoveToGroup={onMoveToGroup}
				onItemReorder={onItemReorder}
			/>
		);
	};

	return (
		<>
			<ReorderableList.Group
				headerContent={(
					<div ref={itemRef}>
						<HubSidebarItemContent
							trackingId="content"
							icon={{
								kind: "font",
								name: "layer-group"
							}}
						>
							{name}
						</HubSidebarItemContent>
					</div>
				)}
				actions={(
					<IconButton
						ref={menuButtonRef}
						icon="ellipsis"
						kind="transparent"
						trackingId="open-actions"
						shape="round"
						onClick={() => onOpenMoreMenu()}
						isActive={menuState.isOpen && !menuState.isContextMenu}
					/>
				)}
				trackingId={generateTrackingId("hub-sidebar-group-item", `|${groupItemClientId}`)}
				mouseInteraction={{
					onContextMenu: onOpenContextMenu
				}}
				isActive={menuState.isOpen}
			>
				{children.length > 0 && (
					<ReorderableList
						onItemMove={canReorderItems ? itemMoveHandler : undefined}
						getOverlayText={getOverlayTextHandler}
						trackingId={generateTrackingId("hub-sidebar-item-list", `|${groupItemClientId}`)}
					>
						{attachable
							.for("groupData")
							.for("children")
							.map(x => (
								x.when(
									doesMatchSearchTerm,
									itemAttachable => itemAttachable.whenType(
										(x) => x.kind === HubItemCategoryNodeKind.Category,
										renderCategoryItem,
										renderGroupItem
									)
								)
							))
						}
					</ReorderableList>
				)}
			</ReorderableList.Group>
			{menuState.isOpen && (
				<GroupItemMenu
					target={ensureNotNil(menuButtonRef.current)}
					onClose={onCloseMenu}
					menuState={menuState}
					onMoveToGroup={(targetGroupId) => onMoveToGroup(groupItemClientId, targetGroupId)}
					onMoveToTop={!isInTopLevel
						? () => onMoveToTop(groupItemClientId)
						: undefined
					}
					groupId={groupItemClientId}
					groupsHierarchy={groupsHierarchy}
					containingGroupId={containingGroupId}
					onCreateCategory={() => onCreateCategory(groupItemClientId)}
					onCreateGroup={() => onCreateGroup(groupItemClientId)}
					onEdit={() =>onEditGroup(groupItemClientId)}
					onDelete={() => onDeleteGroup(groupItemClientId)}
					trackingId={generateTrackingId("open-actions", "__dropover")}
				/>
			)}
		</>
	);
};
HubSidebarGroupItem.displayName = "HubSidebarGroupItem";
